import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Modal } from "../../ui/modal";
import Alert from "../../ui/alert/Alert";
import Spinner from "../../ui/Spinner/Spinner";
import CopyButton from "../../ui/button/copyButton";
import {
  buildPayUrl,
  cancelWalletLink,
  getWalletLink,
  updateWalletLink,
} from "../API/services";
import { UpdateWalletLinkPayload, WalletLinkDetail } from "../types";
import CurrencyFlag from "../CurrencyFlag";
import ConfirmModal from "../ConfirmModal";

interface Props {
  isOpen: boolean;
  linkId: string | null;
  onClose: () => void;
  // Called after an edit or a cancel so the list can refresh.
  onChanged: () => void;
}

const inputClass =
  "w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-900 focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white";

const Row = ({
  label,
  value,
  flag,
}: {
  label: string;
  value: string;
  flag?: string | null;
}) => (
  <div className="flex justify-between gap-4 text-sm">
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span className="flex items-center justify-end gap-2 break-words text-right font-medium text-gray-900 dark:text-white">
      <CurrencyFlag src={flag} className="h-4 w-4" />
      {value}
    </span>
  </div>
);

export const ViewWalletLinkModal = ({
  isOpen,
  linkId,
  onClose,
  onChanged,
}: Props) => {
  const [detail, setDetail] = useState<WalletLinkDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Edit form
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [payerName, setPayerName] = useState("");

  useEffect(() => {
    if (!isOpen || !linkId) return;
    let cancelled = false;
    setDetail(null);
    setEditing(false);
    setError("");
    setLoading(true);
    getWalletLink(linkId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || "Could not load this link.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, linkId]);

  const startEdit = () => {
    if (!detail) return;
    setAmount(String(detail.amount));
    setDescription(detail.description || "");
    setPayerName(detail.payerName || "");
    setError("");
    setEditing(true);
  };

  const handleSave = async () => {
    if (!detail) return;
    const value = Number(amount);
    if (!Number.isInteger(value) || value <= 0)
      return setError("Enter a whole amount greater than 0");

    const payload: UpdateWalletLinkPayload = {
      amount: value,
      description: description.trim(),
      payerName: payerName.trim(),
    };

    try {
      setSaving(true);
      setError("");
      const updated = await updateWalletLink(detail.id, payload);
      setDetail(updated);
      setEditing(false);
      onChanged();
    } catch (err: any) {
      setError(err?.message || "Could not save your changes.");
    } finally {
      setSaving(false);
    }
  };

  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmError, setConfirmError] = useState("");

  const handleCancelLink = async () => {
    if (!detail) return;
    try {
      setCancelling(true);
      setConfirmError("");
      await cancelWalletLink(detail.id);
      setConfirmCancel(false);
      onChanged();
      onClose();
    } catch (err: any) {
      setConfirmError(err?.message || "Could not cancel the link.");
    } finally {
      setCancelling(false);
    }
  };

  const isActive = detail?.status === "ACTIVE";
  const payUrl = detail?.token ? buildPayUrl(detail.token) : "";

  return (
    <>
    <Modal
      isOpen={isOpen && !confirmCancel}
      onClose={onClose}
      className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900 dark:shadow-2xl"
    >
      <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white/90">
        {editing ? "Edit payment link" : "Payment link"}
      </h2>

      {error && (
        <div className="mb-4">
          <Alert variant="error" title="Error" message={error} />
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-10">
          <Spinner className="h-8 w-8 text-blue-500" />
        </div>
      )}

      {detail && !editing && (
        <div className="space-y-4">
          <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
            <Row
              label="Amount"
              value={`${detail.amount.toLocaleString()} ${detail.currency}`}
              flag={detail.flag}
            />
            <Row
              label="Method"
              value={detail.rail === "MOMO" ? "Mobile money" : "Bank transfer"}
            />
            <Row label="Status" value={detail.status} />
            <Row label="Business name" value={detail.creatorName || "-"} />
            <Row label="For" value={detail.payerName || "-"} />
            <Row
              label="Expires"
              value={format(new Date(detail.expiresAt), "MMM d, yyyy h:mm a")}
            />
            <Row
              label="Created"
              value={format(new Date(detail.createdAt), "MMM d, yyyy")}
            />
          </div>

          {detail.description && (
            <div>
              <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">
                Description
              </p>
              <p className="whitespace-pre-wrap break-words text-sm text-gray-900 dark:text-white">
                {detail.description}
              </p>
            </div>
          )}

          <div>
            <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">
              Payment link
            </p>
            {payUrl ? (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                <span className="break-all text-sm text-gray-900 dark:text-white">
                  {payUrl}
                </span>
                <CopyButton text={payUrl} />
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This link cannot be shown again. Create a new link to share.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-3 pt-2">
            {isActive && (
              <button
                onClick={startEdit}
                className="rounded-lg bg-primary px-4 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90"
              >
                Edit
              </button>
            )}
            {isActive && (
              <button
                onClick={() => {
                  setConfirmError("");
                  setConfirmCancel(true);
                }}
                disabled={cancelling}
                className="rounded-lg border border-red-300 px-4 py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                {cancelling ? (
                  <span className="flex items-center justify-center gap-2">
                    <Spinner className="h-4 w-4" />
                    Cancelling...
                  </span>
                ) : (
                  "Cancel link"
                )}
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {detail && editing && (
        <div>
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            The wallet ({detail.currency}) cannot be changed. The link stays the
            same after you save.
          </p>

          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Amount ({detail.currency})
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
              className={inputClass}
            />
          </div>

          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Description (optional)
            </label>
            <textarea
              value={description}
              maxLength={500}
              rows={3}
              onChange={(e) => setDescription(e.target.value)}
              className={inputClass}
            />
          </div>

          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Payer name (optional)
            </label>
            <input
              value={payerName}
              maxLength={100}
              onChange={(e) => setPayerName(e.target.value)}
              className={inputClass}
            />
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-lg bg-primary px-4 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <Spinner className="h-4 w-4" />
                  Saving...
                </span>
              ) : (
                "Save changes"
              )}
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setError("");
              }}
              disabled={saving}
              className="rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Back
            </button>
          </div>
        </div>
      )}

      {!loading && !detail && (
        <button
          onClick={onClose}
          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          Close
        </button>
      )}
    </Modal>

    <ConfirmModal
      isOpen={isOpen && confirmCancel}
      title="Cancel this link?"
      message="Nobody will be able to pay it after this. This cannot be undone."
      confirmText="Yes, cancel link"
      cancelText="Keep link"
      loading={cancelling}
      error={confirmError}
      onConfirm={handleCancelLink}
      onCancel={() => setConfirmCancel(false)}
    />
    </>
  );
};
